<template>
  <div class="trial-monitoring-page">
    <div class="trial-container">
      <div class="trial-header">
        <div class="header-content">
          <h1 class="page-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
              <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
            </svg>
            Suivi des essais gratuits
          </h1>
          <p class="page-subtitle">Monitoring des clients en période d'essai</p>
        </div>
        <router-link :to="{ name: 'adminDashboard' }" class="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
          </svg>
          Retour au dashboard
        </router-link>
      </div>

      <div class="stats-section">
        <div class="stats-card">
          <div class="stat-item">
            <div class="stat-number">{{ trialData?.total_trialing || 0 }}</div>
            <div class="stat-label">Total en essai</div>
          </div>
          <div class="stat-item">
            <div class="stat-number critical">{{ total_3_days_or_less }}</div>
            <div class="stat-label">3 jours ou moins</div>
          </div>
          <div class="stat-item">
            <div class="stat-number warning">{{ total_7_to_4_days }}</div>
            <div class="stat-label">Entre 4 et 7 jours</div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Chargement des données d'essai...</p>
      </div>

      <div v-else class="content-grid">
        <div
          v-for="section in monitoringSections"
          :key="section.days_remaining"
          class="trial-section"
          :class="{
            'critical-section': section.days_remaining <= 3,
            'warning-section': section.days_remaining > 3
          }"
        >
          <div class="section-header">
            <h2 class="section-title">
               <svg v-if="section.days_remaining <= 3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
              </svg>
              {{ section.days_remaining }} jour<span v-if="section.days_remaining > 1">s</span> restant<span v-if="section.days_remaining > 1">s</span>
              <span v-if="section.days_remaining <= 3" class="urgent-badge">URGENT</span>
              <span v-else class="warning-badge">À SURVEILLER</span>
            </h2>
          </div>

          <div v-if="section.customers.length > 0" class="customers-list">
            <div
              v-for="customer in section.customers"
              :key="customer.user_id"
              class="customer-item"
               :class="{
                  'critical-item': section.days_remaining <= 3,
                  'warning-item': section.days_remaining > 3
                }"
            >
              <div class="customer-info">
                <div class="customer-header">
                  <div class="email-container">
                    <span class="customer-email">{{ customer.email }}</span>
                    <span v-if="isFromLP(customer.signup_source)" class="lp-badge">LP</span>
                  </div>
                  <span class="customer-days" :class="{ 'critical': section.days_remaining <= 3, 'warning': section.days_remaining > 3 }">
                    {{ customer.days_remaining }} jour<span v-if="customer.days_remaining !== 1">s</span>
                  </span>
                </div>
                <div class="customer-details">
                  <p class="customer-name">
                    {{ customer.first_name }} {{ customer.last_name }}
                  </p>
                  <div class="customer-meta">
                    <span v-if="customer.phone" class="meta-item">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.6 17.6 0 0 0 4.168 6.608 17.6 17.6 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.68.68 0 0 0-.58-.122l-2.19.547a1.75 1.75 0 0 1-1.657-.459L5.482 8.062a1.75 1.75 0 0 1-.46-1.657l.548-2.19a.68.68 0 0 0-.122-.58zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z"/>
                      </svg>
                      {{ customer.phone }}
                    </span>
                    <span v-if="customer.school" class="meta-item">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8.211 2.047a.5.5 0 0 0-.422 0l-7.5 3.5a.5.5 0 0 0 .025.917l7.5 3a.5.5 0 0 0 .372 0L14 7.14V13a1 1 0 0 0-1 1v2h3v-2a1 1 0 0 0-1-1V6.739l.686-.275a.5.5 0 0 0 .025-.917zM8 8.46 1.758 5.965 8 3.052l6.242 2.913z"/>
                        <path d="M4.176 9.032a.5.5 0 0 0-.656.327l-.5 1.7a.5.5 0 0 0 .294.605l4.5 1.8a.5.5 0 0 0 .372 0l4.5-1.8a.5.5 0 0 0 .294-.605l-.5-1.7a.5.5 0 0 0-.656-.327L8 10.466z"/>
                      </svg>
                      {{ customer.school }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="customer-status">
                <div class="trial-end">Fin: {{ formatDate(customer.trial_end_date) }}</div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
              <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
            </svg>
            <p>Aucun client avec {{ section.days_remaining }} jour<span v-if="section.days_remaining !== 1">s</span> restant<span v-if="section.days_remaining !== 1">s</span></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";

export default {
  name: 'TrialMonitoring',
  data() {
    return {
      loading: true,
      trialData: null,
      adminPassword: null,
    }
  },
  computed: {
    monitoringSections() {
      return this.trialData?.trial_monitoring?.filter(section => section.count > 0) || [];
    },
    total_3_days_or_less() {
      if (!this.trialData?.trial_monitoring) return 0;
      return this.trialData.trial_monitoring
        .filter(section => section.days_remaining <= 3)
        .reduce((sum, section) => sum + section.count, 0);
    },
    total_7_to_4_days() {
      if (!this.trialData?.trial_monitoring) return 0;
      return this.trialData.trial_monitoring
        .filter(section => section.days_remaining > 3 && section.days_remaining <= 7)
        .reduce((sum, section) => sum + section.count, 0);
    }
  },
  methods: {
    isFromLP(source) {
      return source && source.includes('LP');
    },

    promptForPassword() {
      if (!this.adminPassword) {
        const password = prompt('Mot de passe administrateur requis:');
        if (!password) {
          throw new Error('Mot de passe requis pour accéder aux données administrateur');
        }
        this.adminPassword = password;
      }
      return this.adminPassword;
    },

    async fetchTrialData() {
      this.loading = true
      try {
        const { data } = await supabase.auth.getSession()
        const jwt = data?.session?.access_token

        if (!jwt) {
          throw new Error('Session expirée')
        }

        const adminPassword = this.promptForPassword();

        const response = await fetch('https://api.query-juriste.com/copilot/admin/get-trial-monitoring', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
            'X-Admin-Password': adminPassword
          }
        })

        if (!response.ok) {
          if (response.status === 403) {
            this.adminPassword = null;
          }
          throw new Error('Erreur lors de la récupération des données')
        }

        const result = await response.json()
        this.trialData = result
      } catch (error) {
        console.error('Error fetching trial data:', error)
        this.$toast.error(error.message || 'Erreur lors du chargement des données d\'essai')
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  },

  async mounted() {
    await this.fetchTrialData()
  }
}
</script>

<style scoped>
.trial-monitoring-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem;
}

.trial-container {
  max-width: 1200px;
  margin: 0 auto;
}

.trial-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  color: white;
}

.header-content {
  flex: 1;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.stats-section {
  margin-bottom: 2rem;
}

.stats-card {
  display: flex;
  gap: 2rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  display: block;
  font-size: 2.5rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 0.5rem;
}

.stat-number.critical {
  color: #e53e3e;
}

.stat-number.warning {
  color: #dd6b20;
}

.stat-label {
  display: block;
  font-size: 0.9rem;
  color: #718096;
  font-weight: 600;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  text-align: center;
  color: white;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 3px solid white;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

.trial-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.critical-section {
  border-left: 4px solid #e53e3e;
}

.warning-section {
  border-left: 4px solid #dd6b20;
}

.section-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
}

.urgent-badge {
  padding: 4px 12px;
  background: #e53e3e;
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 1rem;
  text-transform: uppercase;
}

.warning-badge {
  padding: 4px 12px;
  background: #dd6b20;
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 1rem;
  text-transform: uppercase;
}

.customers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.customer-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.critical-item {
  border-left: 4px solid #e53e3e;
  background: #fef5f5;
}

.warning-item {
  border-left: 4px solid #dd6b20;
  background: #fffaf0;
}

.customer-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.customer-info {
  flex: 1;
  overflow: hidden;
}

.customer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.email-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.customer-email {
  font-weight: 600;
  color: #2d3748;
  font-size: 1rem;
}

.lp-badge {
  padding: 3px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.customer-days {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.customer-days.critical {
  background: #e53e3e;
  color: white;
}

.customer-days.warning {
  background: #dd6b20;
  color: white;
}

.customer-details {
  overflow: hidden;
}

.customer-name {
  margin: 0 0 0.5rem 0;
  font-weight: 500;
  color: #4a5568;
  font-size: 0.95rem;
}

.customer-meta {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #718096;
}

.customer-status {
  flex-shrink: 0;
  margin-left: 1rem;
  text-align: right;
}

.trial-end {
  font-size: 0.8rem;
  color: #718096;
  font-weight: 500;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: #a0aec0;
  background: #f7fafc;
  border-radius: 12px;
}

.empty-state svg {
  opacity: 0.5;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .trial-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .page-title {
    font-size: 2rem;
  }

  .stats-card {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
  }

  .content-grid {
      grid-template-columns: 1fr;
  }

  .customer-item {
    flex-direction: column;
    align-items: stretch;
  }

  .customer-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .customer-meta {
    flex-direction: column;
    gap: 0.5rem;
  }

  .customer-status {
    margin-left: 0;
    margin-top: 1rem;
    text-align: left;
  }
}
</style>
